import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getMyMessages, createMessage, getConversation, markMessagesAsRead } from '../api/messages';
import { apiFetch } from '../lib/api';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';

const Messages = () => {
  const { user } = useAuth();
  const { isExpanded } = useSidebar();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    // Rozetin hemen kaybolması için yerel state'i anında güncelle
    setConversations(prev => prev.map(c => c.id === conversation.id ? { ...c, unreadCount: 0 } : c));
  };

  useEffect(() => {
    // Worker üzerinden verileri çek
    const fetchConversations = async () => {
      try {
        if (!user?.id) return;

        // Worker üzerinden mesajları çek
        const allMessages = await getMyMessages();

        // Tüm unique karşı taraf kullanıcı ID'lerini topla
        const userIds = new Set();
        allMessages?.forEach(msg => {
          const otherUserId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
          if (otherUserId) userIds.add(otherUserId);
        });

        // Her kullanıcı için profil bilgilerini çek
        const conversationsData = await Promise.all(
          Array.from(userIds).map(async (userId) => {
            // Gerçek profile bilgilerini çek
            let profile;
            try {
              const profileResponse = await apiFetch(`/api/profiles?id=eq.${userId}&select=id,first_name,last_name,email`);
              const profileData = await profileResponse.json();
              profile = profileData[0] || { id: userId, first_name: 'User', last_name: 'Name', email: 'user@example.com' };
            } catch (error) {
              console.error('Profile fetch error:', error);
              profile = { id: userId, first_name: 'User', last_name: 'Name', email: 'user@example.com' };
            }

            if (!profile) return null;

            // Bu kullanıcı ile olan mesajları bul
            const conversationMessages = (allMessages || [])
              .filter(m => (m.sender_id === user.id && m.receiver_id === userId) || (m.sender_id === userId && m.receiver_id === user.id))
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            const lastMessage = conversationMessages[0];
            const unreadCount = (conversationMessages || []).filter(m => m.receiver_id === user.id && !m.is_read).length;

            return {
              id: userId,
              user: {
                id: profile.id,
                name: `${profile.first_name} ${profile.last_name}`,
                email: profile.email,
                avatar: profile.first_name?.[0] || 'U'
              },
              lastMessage: lastMessage?.content || 'Henüz mesaj yok. Konuşmayı başlatın!',
              lastMessageTime: lastMessage ? new Date(lastMessage.created_at).toLocaleDateString('tr-TR', { 
                day: 'numeric', 
                month: 'short' 
              }) : '',
              unreadCount
            };
          })
        );

        // Null olmayan konuşmaları filtrele ve son mesaj zamanına göre sırala
        const validConversations = conversationsData
          .filter(conv => conv !== null)
          .sort((a, b) => {
            if (!a.lastMessageTime && !b.lastMessageTime) return 0;
            if (!a.lastMessageTime) return 1;
            if (!b.lastMessageTime) return -1;
            return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
          });

        setConversations(validConversations);
        
        // localStorage'dan selectedUserId al
        const selectedUserId = localStorage.getItem('selectedUserId');
        
        if (selectedUserId) {
          const targetConversation = validConversations.find(conv => conv.user.id === selectedUserId);
          
          if (targetConversation) {
            setSelectedConversation(targetConversation);
          } else {
            // Eğer konuşma yoksa, yeni konuşma oluştur
            let profile;
            try {
              const profileResponse = await apiFetch(`/api/profiles?id=eq.${selectedUserId}&select=id,first_name,last_name,email`);
              const profileData = await profileResponse.json();
              profile = profileData[0] || { id: selectedUserId, first_name: 'User', last_name: 'Name', email: 'user@example.com' };
            } catch (error) {
              console.error('Profile fetch error:', error);
              profile = { id: selectedUserId, first_name: 'User', last_name: 'Name', email: 'user@example.com' };
            }
            
            if (profile) {
              const newConversation = {
                id: selectedUserId,
                user: {
                  id: profile.id,
                  name: `${profile.first_name} ${profile.last_name}`,
                  email: profile.email,
                  avatar: profile.first_name?.[0] || 'U'
                },
                lastMessage: 'Henüz mesaj yok. Konuşmayı başlatın!',
                lastMessageTime: '',
                unreadCount: 0
              };
              setSelectedConversation(newConversation);
              
              // Yeni konuşmayı conversations listesine de ekle
              setConversations([newConversation, ...validConversations]);
            }
          }
          
          // localStorage'ı temizle
          localStorage.removeItem('selectedUserId');
        }
        
        setLoading(false);
      } catch (error) {
        logger.error('Konuşmalar çekilirken hata:', error);
        setLoading(false);
      }
    };

    fetchConversations();
  }, [user?.id]);

  useEffect(() => {
    if (selectedConversation) {
      // Supabase'den gerçek mesajları çek
      const fetchMessages = async () => {
        try {
          if (!user?.id || !selectedConversation?.user?.id) return;

          const data = await getConversation(user.id, selectedConversation.user.id, null);
          const formattedMessages = data?.map(msg => ({
            id: msg.id,
            sender: msg.sender_id === user.id ? 'me' : 'other',
            content: msg.content,
            timestamp: new Date(msg.created_at).toLocaleTimeString('tr-TR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            }),
            isRead: true
          })) || [];

          setMessages(formattedMessages);

          // Bu görüşmedeki bana gelen ve okunmamış mesajları okundu yap
          const unreadMessageIds = data
            ?.filter(msg => msg.receiver_id === user.id && !msg.is_read)
            ?.map(msg => msg.id) || [];
          
          if (unreadMessageIds.length > 0) {
            try {
              await markMessagesAsRead(unreadMessageIds);
              window.dispatchEvent(new Event('refresh-unread'));
            } catch (error) {
              console.error('Mark messages as read error:', error);
            }
          }
        } catch (error) {
          logger.error('Mesajlar çekilirken hata:', error);
        }
      };

      fetchMessages();
    }
  }, [selectedConversation, user?.id]);

  const handleSendMessage = async () => {
    if (newMessage.trim() && selectedConversation && user?.id) {
      try {
          await createMessage({
            receiver_id: selectedConversation.user.id,
            content: newMessage
            // sender_id Worker'da otomatik ekleniyor
            // app: null filtreleniyor
          });

        // Gönderildiğinde karşı tarafta unread artacağı için Dashboard’a haber ver
        window.dispatchEvent(new Event('refresh-unread'));

        // Mesajı local state'e ekle
        const newMsg = {
          id: Date.now().toString(),
          sender: 'me',
          content: newMessage,
          timestamp: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          isRead: true
        };
        setMessages([...messages, newMsg]);
        setNewMessage('');

        // Konuşmaları güncelle
        setConversations(conversations.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, lastMessage: newMessage, lastMessageTime: new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) }
            : conv
        ));

      } catch (error) {
        logger.error('Mesaj gönderilirken hata:', error);
        toast.error('Mesaj gönderilirken hata oluştu!');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="relative">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-emerald-600"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Sparkles className="h-8 w-8 text-emerald-600 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Mobile Header Gradient */}
      <div className="md:hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-32 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Desktop Header Gradient */}
      <div className="hidden md:block bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 h-32 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className={`relative -mt-16 pb-4 transition-all duration-300 ${
        isExpanded ? 'md:ml-72' : 'md:ml-18'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[calc(100vh-8rem)] flex flex-col md:flex-row">
          {/* Conversations List */}
          <div className="w-full md:w-80 backdrop-blur-xl bg-white/60 md:border-r border-white/30 flex flex-col md:rounded-l-2xl rounded-t-2xl md:rounded-tr-none shadow-lg">
            <div className="p-6 border-b border-white/30">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Konuşmalar</h2>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="flex items-center justify-center h-full p-4">
                  <div className="text-center">
                    <MessageSquare className="mx-auto h-16 w-16 text-emerald-300" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">Henüz konuşma yok</h3>
                    <p className="mt-2 text-sm text-gray-600">
                      Başka kullanıcılarla mesajlaşmaya başlayın
                    </p>
                  </div>
                </div>
              ) : (
                conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => handleSelectConversation(conversation)}
                  className={`p-4 cursor-pointer hover:bg-white/40 transition-colors duration-200 border-b border-white/20 ${
                    selectedConversation?.id === conversation.id ? 'bg-emerald-100/60 border-r-4 border-emerald-500' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="relative w-12 h-12">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-sm font-semibold text-white">
                            {conversation.user.avatar}
                          </span>
                        </div>
                        {conversation.unreadCount > 0 && (
                          <span className="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full shadow">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {conversation.user.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {conversation.lastMessageTime}
                        </p>
                      </div>
                      <p className="text-sm text-gray-600 truncate mt-1">
                        {conversation.lastMessage}
                      </p>
                    </div>
                    {/* Sağ taraftaki eski rozet kaldırıldı; rozet avatar üzerinde gösteriliyor */}
                  </div>
                </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col backdrop-blur-xl bg-white/60 md:rounded-r-2xl rounded-b-2xl shadow-lg border border-white/30 mt-4 md:mt-0">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-sm border-b border-white/30 px-6 py-4 rounded-tr-2xl">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-sm font-semibold text-white">
                        {selectedConversation.user.avatar}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedConversation.user.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedConversation.user.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <MessageSquare className="mx-auto h-16 w-16 text-emerald-300" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">Henüz mesaj yok</h3>
                        <p className="mt-2 text-sm text-gray-600">
                          Konuşmayı başlatın!
                        </p>
                      </div>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.sender === 'me' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-sm ${
                            message.sender === 'me'
                              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-br-md'
                              : 'bg-white/60 backdrop-blur-sm text-gray-900 rounded-bl-md border border-white/40'
                          }`}
                        >
                          <p className="text-sm leading-relaxed">{message.content}</p>
                          <p className={`text-xs mt-2 ${
                            message.sender === 'me' ? 'text-emerald-100' : 'text-gray-500'
                          }`}>
                            {message.timestamp}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Message Input */}
                <div className="backdrop-blur-sm bg-white/40 border-t border-white/30 px-6 py-4 rounded-br-2xl">
                  <div className="flex items-end space-x-3">
                    <div className="flex-1">
                      <textarea
                        rows="1"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Bir mesaj yazın..."
                        className="block w-full px-4 py-3 backdrop-blur-sm bg-white/60 border border-white/40 rounded-2xl leading-5 text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 resize-none"
                        style={{ minHeight: '48px', maxHeight: '120px' }}
                      />
                    </div>
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="inline-flex items-center justify-center w-12 h-12 rounded-full text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="mx-auto h-16 w-16 text-emerald-300" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">Mesaj seçilmedi</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Sol taraftan bir konuşma seçin
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;